import { Grid, Snackbar, SnackbarOrigin, TextField, Typography } from '@material-ui/core'
import React, { useState } from 'react'
import { dashboardStyles } from './DashboardStyles'
import Button from '@material-ui/core/Button'
import { useDispatch, useSelector } from 'react-redux'
import { Alert } from '@material-ui/lab'
import { useTranslation } from 'react-i18next'
import { updateAdmin, updatePassword } from '../../redux/actions/dashboard'
import { setUser } from '../../shared/redux/actions/common'
import { config } from '../../config'
import EyeIcon from '../../shared/static/icon-eye-white.svg'
import EyeOffIcon from '../../shared/static/icon-eye-off-white.svg'
import Reports from './Reports'
import Markets from './Markets'
import TenantOverview from './TenantOverview'
import OwnTenantUsers from './OwnTenantUsers'
import { useIsSuperAdmin } from '../../utils/tenantFilter'

const validatePass = (pass: string) => /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/.test(pass)

export default function Dashboard() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const classes = dashboardStyles()
    const isSuperAdmin = useIsSuperAdmin()
    const [errorMessage, setErrorMessage] = useState<string>()
    const [successMsg, setSuccessMsg] = useState<string>()
    const admin = useSelector((state: any) => state.login.value.user)
    const adminContact = useSelector((state: any) => state.login.value.user.contact)
    const id = useSelector((state: any) => state.login.value.user.id)
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [contact, setContact] = useState(adminContact || '')
    const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>()
    const [showNewPassword, setShowNewPassword] = useState<boolean>()

    const snackBar = (msg: string, onClose: () => void, type: 'error' | 'success') => {
        const position =
            type === 'success'
                ? { vertical: 'top', horizontal: 'center' }
                : { vertical: 'bottom', horizontal: 'center' }
        return (
            <Snackbar anchorOrigin={position as SnackbarOrigin} open={true} autoHideDuration={5000} onClose={onClose}>
                <Alert onClose={onClose} severity={type}>
                    {msg}
                </Alert>
            </Snackbar>
        )
    }

    const changeNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword)
    }

    const changeCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPassword)
    }

    const handleSave = async () => {
        if (currentPassword) {
            if (!validatePass(newPassword)) {
                setErrorMessage(t('dashboard.errorPassValidation'))
                return
            }
        }

        let ok = true
        let changed = false

        if (contact !== adminContact) {
            changed = true
            const adminBody = {
                ...admin,
                contact,
            }
            delete adminBody['password']

            const updatedUser = await updateAdmin(id, adminBody).catch(() => {
                ok = false
                setErrorMessage(t('dashboard.errorUpdatingAbout'))
            })
            if (updatedUser) dispatch(setUser(updatedUser))
        }

        if (newPassword) {
            changed = true
            const data = {
                currentPassword,
                newPassword,
            }
            // backend vracia 400 aj pri nespravnom aktualnom hesle
            const passwordOk = await updatePassword(data)
                .then(() => true)
                .catch(() => {
                    setErrorMessage(t('dashboard.errorUpdatingPass'))
                    return false
                })
            if (passwordOk) {
                setCurrentPassword('')
                setNewPassword('')
            } else {
                ok = false
            }
        }

        if (changed && ok) setSuccessMsg(t('dashboard.successfullyUpdated'))
    }

    return (
        <div className={classes.page}>
            <Grid container>
                <Grid item xs={12} className={classes.headerWrapper}>
                    <Typography align="left" variant="h3">
                        {t('dashboard.title')}
                    </Typography>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={4}>
                    <Grid className={classes.headerWrapper}>
                        <Typography align="left" variant="h6">
                            {t('dashboard.changePass')}
                        </Typography>
                    </Grid>
                    <Grid container className={classes.passwordSection}>
                        <Grid item xs={6} className={classes.passwordWrapper}>
                            <Grid>
                                <TextField
                                    name="Current password"
                                    className={classes.input}
                                    type={currentPassword && !showCurrentPassword ? 'password' : 'text'}
                                    variant="filled"
                                    label={t('dashboard.currentPass')}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                />
                            </Grid>
                            {currentPassword ? (
                                <Grid item>
                                    <div onClick={changeCurrentPasswordVisibility}>
                                        {showCurrentPassword ? (
                                            <img alt="Eye" src={EyeIcon} className={classes.inputIconRight} />
                                        ) : (
                                            <img alt="Eye" src={EyeOffIcon} className={classes.inputIconRight} />
                                        )}
                                    </div>
                                </Grid>
                            ) : (
                                ''
                            )}
                        </Grid>
                        <Grid item xs={6} className={classes.passwordWrapper}>
                            <Grid>
                                <TextField
                                    disabled={!currentPassword}
                                    className={classes.input}
                                    name="New password"
                                    type={newPassword && !showNewPassword ? 'password' : 'text'}
                                    variant="filled"
                                    label={t('common.newPassword')}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                            </Grid>
                            {newPassword ? (
                                <Grid item>
                                    <div onClick={changeNewPasswordVisibility}>
                                        {showNewPassword ? (
                                            <img alt="Eye" src={EyeIcon} className={classes.inputIconRight} />
                                        ) : (
                                            <img alt="Eye" src={EyeOffIcon} className={classes.inputIconRight} />
                                        )}
                                    </div>
                                </Grid>
                            ) : (
                                ''
                            )}
                        </Grid>
                    </Grid> 
                </Grid>
                {tenantId !== config.mainTenantId ? (
                    <Grid item xs={6}>
                        <Grid className={classes.headerWrapper}>
                            <Typography align="left" variant="h6">
                                {t('dashboard.contact')}
                            </Typography>
                        </Grid>
                        <TextField
                            className={classes.inputFull}
                            name="about"
                            inputProps={{
                                maxLength: 1000,
                            }}
                            type="text"
                            variant="filled"
                            label={t('dashboard.contact')}
                            value={contact}
                            onChange={(e) => setContact(e.target.value)}
                        />
                    </Grid>
                ) : (
                    ''
                )}
                <Grid item xs={2}>
                    <Button
                        className={classes.button}
                        variant="contained"
                        color="primary"
                        component="span"
                        onClick={handleSave}
                    >
                        {t('dashboard.save')}
                    </Button>
                </Grid>
                <Grid item xs={12} className={classes.section}>
                    {/* superadmin: loginy a reporty vsetkych tenantov zoskupene podla tenanta (s editaciou),
                        ostatni: loginy svojho tenanta (nastavenie hesla) a svoje reporty (len citanie) */}
                    {isSuperAdmin ? (
                        <TenantOverview onSuccess={setSuccessMsg} onError={setErrorMessage} />
                    ) : (
                        <>
                            <OwnTenantUsers onSuccess={setSuccessMsg} onError={setErrorMessage} />
                            <Reports />
                        </>
                    )}
                    {tenantId === config.mainTenantId ? (
                        <Markets editable={isSuperAdmin} onSuccess={setSuccessMsg} onError={setErrorMessage} />
                    ) : (
                        ''
                    )}
                </Grid>
            </Grid>
            {errorMessage && snackBar(errorMessage, () => setErrorMessage(undefined), 'error')}
            {successMsg && !errorMessage && snackBar(successMsg, () => setSuccessMsg(undefined), 'success')}
        </div>
    )
}